import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";

const SITE = "https://www.regard-de-nature.fr";

const ConsultationsVisio = () => {
  useSEO({
    title: "Naturopathe en visio – Consultations en ligne partout en France",
    description:
      "Consultation de naturopathie en visioconférence avec Laura Elie. Un accompagnement personnalisé depuis chez vous, partout en France : confort, simplicité, flexibilité.",
    canonical: `${SITE}/consultations-visio-naturopathe-france`,
  });

  return (
    <SeoPageLayout
      h1="Consultation de naturopathie en ligne, où que vous soyez"
      intro="Quand se déplacer devient compliqué — parents débordés, emploi du temps chargé, distance, enfants à gérer — profitez d'un accompagnement naturopathique depuis chez vous, partout en France."
    >
      <SeoSection title="Naturopathe en visio : un suivi adapté à votre quotidien">
        <p>
          Vous recherchez un naturopathe en visio ? Vous habitez loin du cabinet ou votre emploi du temps rend
          les déplacements compliqués ?
        </p>
        <p>
          Les consultations de naturopathie en visioconférence permettent de bénéficier d'un accompagnement
          personnalisé depuis chez vous, partout en France. Que vous soyez à Toulouse, Bordeaux, Lyon, Paris,
          Marseille ou dans une autre région, vous pouvez profiter d'un suivi naturopathique adapté à vos besoins
          sans avoir à vous déplacer.
        </p>
        <p>
          La consultation en ligne offre la même qualité d'écoute, d'échange et d'accompagnement qu'une
          consultation en cabinet, tout en vous apportant davantage de confort et de flexibilité.
        </p>
      </SeoSection>

      <SeoSection title="Pourquoi choisir une consultation de naturopathie en visio ?">
        <p>La naturopathie en ligne présente de nombreux avantages :</p>
        <SeoList
          items={[
            "Aucun trajet à prévoir",
            "Un gain de temps précieux au quotidien",
            "Plus besoin d'organiser une garde pour les enfants",
            "Un rendez-vous dans un environnement familier et rassurant",
            "Une solution accessible partout en France",
            "La même qualité d'accompagnement qu'en présentiel",
          ]}
        />
        <p>
          La visio permet également de limiter la fatigue liée aux déplacements et de faciliter l'organisation
          des rendez-vous, notamment lorsque le quotidien est déjà bien rempli.
        </p>
      </SeoSection>

      <SeoSection title="Une solution particulièrement adaptée aux familles">
        <p>
          Les consultations en visio sont particulièrement appréciées des parents d'enfants atypiques,
          hypersensibles, TDAH ou présentant des troubles DYS.
        </p>
        <p>
          Lorsque les déplacements représentent une source de stress supplémentaire ou nécessitent une
          organisation complexe, la consultation à distance permet de bénéficier d'un accompagnement sereinement,
          depuis le domicile familial.
        </p>
        <p>
          Elle offre également davantage de souplesse pour les familles confrontées à une charge mentale
          importante, à des emplois du temps chargés ou à des besoins spécifiques liés à l'enfant.
        </p>
      </SeoSection>

      <SeoSection title="Comment se déroule une consultation de naturopathie en ligne ?">
        <p>
          La consultation se déroule en visioconférence, dans les mêmes conditions qu'un rendez-vous en cabinet.
        </p>
        <p>
          Lors du bilan naturopathique, nous prenons le temps d'échanger sur votre mode de vie, votre
          alimentation, votre sommeil, votre niveau de stress, votre vitalité et vos objectifs de mieux-être.
        </p>
        <p>
          À l'issue de la consultation, vous recevez un programme d'hygiène de vie personnalisé comprenant des
          conseils adaptés à votre situation et à votre quotidien.
        </p>
      </SeoSection>

      <SeoSection title="Pour quels accompagnements ?">
        <p>Les consultations de naturopathie en visio conviennent à de nombreuses situations :</p>
        <SeoList
          items={[
            "Fatigue et manque d'énergie",
            "Stress et surcharge mentale",
            "Troubles du sommeil",
            "Troubles digestifs",
            "Accompagnement des parents",
            "Soutien des familles d'enfants atypiques",
            "Recherche d'un meilleur équilibre de vie",
            "Prévention et amélioration de l'hygiène de vie",
          ]}
        />
      </SeoSection>

      <SeoSection title="Consultation naturopathe à distance : partout en France">
        <p>
          Grâce à la visioconférence, il n'est plus nécessaire d'habiter à proximité du cabinet pour bénéficier
          d'un accompagnement naturopathique.
        </p>
        <p>
          Que vous viviez en ville ou à la campagne, dans une grande métropole ou dans une région plus isolée,
          je vous accompagne à distance avec la même attention, la même bienveillance et la même qualité
          d'écoute qu'en présentiel.
        </p>
        <p>
          Prenez rendez-vous pour votre consultation de naturopathie en visio et bénéficiez d'un accompagnement
          personnalisé, où que vous soyez en France.
        </p>
        <p className="mt-4">
          Vous préférez le présentiel ? Je consulte à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          , dans le Lauragais.
        </p>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default ConsultationsVisio;
